import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import p from 'x/config/platform-specific'

export type WebBackHandlerContextType = {
  isOverlayOpen: boolean
  setOverlayOpen: (value: boolean) => void
  isTopOfStack: boolean
  setIsTopOfStack: (value: boolean) => void
  setGoBackFn: (fn: () => void) => void
  setCloseOverlayFn?: (fn: () => void) => void
}

export const WebBackHandlerContext = createContext<WebBackHandlerContextType | null>(null)

export const useWebBackHandler = (): WebBackHandlerContextType => {
  const context = useContext(WebBackHandlerContext)
  // if (!context) {
  //   throw new Error('useWebBackHandler must be used within WebBackHandlerProvider')
  // }
  return context ? context : ({} as WebBackHandlerContextType)
}

export const WebBackHandlerProvider = ({ children }: { children: ReactNode }) => {
  const [isOverlayOpen, setOverlayOpenState] = useState(false)
  const [isTopOfStack, setIsTopOfStack] = useState(true)

  const isOverlayOpenRef = useRef(false)
  const goBackRef = useRef<() => void>(() => {})
  const closeOverlayRef = useRef<() => void>(() => {})
  const isExitingRef = useRef(false) // 🔐 ป้องกัน loop

  const setOverlayOpen = (value: boolean) => {
    isOverlayOpenRef.current = value
    setOverlayOpenState(value)
  }

  const handleBack = useCallback(
    async (isBrowserBack = false) => {
      if (isExitingRef.current) return

      if (isOverlayOpenRef.current) {
        closeOverlayRef.current?.()
        return
      }

      if (!isTopOfStack) {
        goBackRef.current?.()
        return
      }

      if (isBrowserBack) {
        const isConfirmed = await p.op.isUserConfirm('คุณต้องการออกจากแอปหรือไม่?')
        if (isConfirmed) {
          isExitingRef.current = true
          window.removeEventListener('popstate', handlePopState)
          window.history.go(-1)
        } else {
          window.history.pushState(null, '', window.location.pathname)
        }
      }
    },
    [isTopOfStack]
  )

  const handlePopState = useCallback(
    (e: PopStateEvent) => {
      e.preventDefault?.()
      handleBack(true)
    },
    [handleBack]
  )

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        handleBack()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('popstate', handlePopState)
    window.history.pushState(null, '', window.location.pathname)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [handleBack, handlePopState])

  const value: WebBackHandlerContextType = {
    isOverlayOpen,
    setOverlayOpen,
    isTopOfStack,
    setIsTopOfStack: (value) => setIsTopOfStack(value),
    setGoBackFn: (fn: () => void) => {
      goBackRef.current = fn
    },
    setCloseOverlayFn: (fn: () => void) => {
      closeOverlayRef.current = fn ?? (() => {})
    },
  }

  return <WebBackHandlerContext.Provider value={value}>{children}</WebBackHandlerContext.Provider>
}
